import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface CreateNotesFieldsProps {
    note: string;
    setNote: (value: string) => void;
    vendorNote: string;
    setVendorNote: (value: string) => void;
    noteMaxLength: number;
    busy: boolean;
    show: boolean;
    vendorId: number | null;
    createKey: number;
    isLoadingDraft: boolean;
    onDuplicateNoteToVendor: () => void;
}

const CreateNotesFields = ({
    note,
    setNote,
    vendorNote,
    setVendorNote,
    noteMaxLength,
    busy,
    show,
    vendorId,
    createKey,
    isLoadingDraft,
    onDuplicateNoteToVendor,
}: CreateNotesFieldsProps) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const [showNote, setShowNote] = useState(false);
    const [showVendorNote, setShowVendorNote] = useState(false);

    useEffect(() => {
        if (!show || isLoadingDraft) return;
        setShowNote(Boolean(String(note || '').trim()));
        setShowVendorNote(Boolean(String(vendorNote || '').trim()));
        // Expand notes only when the create/clone session finishes loading
        // eslint-disable-next-line react-hooks/exhaustive-deps -- note values read at load boundary
    }, [show, vendorId, createKey, isLoadingDraft]);

    return (
        <div className="mx-3 mb-3">
            <div className="mb-2">
                <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="d-inline-block p-0 me-3 mb-1 align-baseline"
                    disabled={busy}
                    onClick={() => setShowNote((prev) => !prev)}
                >
                    <i className="bi bi-sticky me-1" aria-hidden="true" />
                    {t('supplierRequests.create.addNote')}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="d-inline-block p-0 mb-1 align-baseline"
                    disabled={busy}
                    onClick={() => setShowVendorNote((prev) => !prev)}
                >
                    <i className="bi bi-truck me-1" aria-hidden="true" />
                    {t('supplierRequests.create.addVendorNote')}
                </Button>
            </div>

            {showNote && (
                <Form.Group className="mb-3">
                    <label className="form-label text-muted">
                        {t('supplierRequests.create.noteLabel')}
                    </label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={note}
                        disabled={busy}
                        maxLength={noteMaxLength}
                        placeholder={t('supplierRequests.create.notePlaceholder')}
                        onChange={(e) => setNote(e.target.value)}
                    />
                    <div className="form-text text-end">
                        {note.length}/{noteMaxLength}
                    </div>
                </Form.Group>
            )}

            {showVendorNote && (
                <Form.Group>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="form-label text-muted mb-0">
                            {t('supplierRequests.create.vendorNoteLabel')}
                        </label>
                        <Button
                            type="button"
                            size="sm"
                            variant="link"
                            className="p-0 align-baseline"
                            disabled={busy || !note.trim()}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                onDuplicateNoteToVendor();
                            }}
                        >
                            <i className="bi bi-files me-1" aria-hidden="true" />
                            {t('supplierRequests.create.duplicateNote')}
                        </Button>
                    </div>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={vendorNote}
                        disabled={busy}
                        maxLength={noteMaxLength}
                        placeholder={t('supplierRequests.create.vendorNotePlaceholder')}
                        onChange={(e) => setVendorNote(e.target.value)}
                    />
                    <div className="form-text text-end">
                        {vendorNote.length}/{noteMaxLength}
                    </div>
                </Form.Group>
            )}
        </div>
    );
};

export default CreateNotesFields;
